/**
 * Status effect definitions + pure helpers.
 *
 * Status effects are stored as a StatusMap (kind -> stacks). All mutation goes
 * through these helpers so the rules stay in ONE place and are exhaustively
 * testable. Nothing here touches the RNG, DOM, or time.
 */

import type { StatusDef, StatusKind, StatusMap } from "./types";

export const STATUS_DEFS: Record<StatusKind, StatusDef> = {
  block: { kind: "block", behavior: "intensity", kind_class: "buff" },
  vulnerable: { kind: "vulnerable", behavior: "duration", kind_class: "debuff" },
  weak: { kind: "weak", behavior: "duration", kind_class: "debuff" },
  exposed: { kind: "exposed", behavior: "persistent", kind_class: "debuff" },
  virus: { kind: "virus", behavior: "intensity", kind_class: "debuff" },
  poison: { kind: "poison", behavior: "intensity", kind_class: "debuff" },
  strength: { kind: "strength", behavior: "persistent", kind_class: "buff" },
  dexterity: { kind: "dexterity", behavior: "persistent", kind_class: "buff" },
  regen: { kind: "regen", behavior: "intensity", kind_class: "buff" },
  intangible: { kind: "intangible", behavior: "duration", kind_class: "buff" },
  firewall: { kind: "firewall", behavior: "persistent", kind_class: "buff" },
  overclock: { kind: "overclock", behavior: "duration", kind_class: "buff" },
};

/** Tunable constants for status math (kept here so balance lives in one spot). */
export const STATUS_TUNING = {
  vulnerableMultiplier: 1.5, // damage multiplier while vulnerable
  weakMultiplier: 0.75, // outgoing damage multiplier while weak
  exposedFlatPerStack: 1, // extra flat damage taken per exposed stack
  intangibleCap: 1, // max damage per hit while intangible
} as const;

export function getStacks(s: StatusMap | undefined, k: StatusKind): number {
  if (!s) return 0;
  return s[k] ?? 0;
}

/**
 * Add (or subtract, if negative) stacks. Returns a NEW StatusMap. Clamps at 0
 * and removes the key entirely when it reaches 0, keeping state minimal.
 * Note: `block` is allowed to stack without an upper bound.
 */
export function addStatus(
  s: StatusMap | undefined,
  k: StatusKind,
  delta: number,
): StatusMap {
  const out: StatusMap = { ...(s ?? {}) };
  const cur = out[k] ?? 0;
  const nextVal = cur + delta;
  if (nextVal <= 0) {
    delete out[k];
  } else {
    out[k] = nextVal;
  }
  return out;
}

/** Hard-set a status to an exact value (used by some intents/cards). */
export function setStatus(
  s: StatusMap | undefined,
  k: StatusKind,
  value: number,
): StatusMap {
  const out: StatusMap = { ...(s ?? {}) };
  if (value <= 0) delete out[k];
  else out[k] = value;
  return out;
}

/**
 * Compute outgoing damage from a base value, given the attacker's statuses.
 * Order: + strength (flat per stack), then weak multiplier (rounded down).
 * Block/vulnerable belong to the *defender* and are applied in applyDamage.
 */
export function computeOutgoingDamage(
  base: number,
  attacker: StatusMap | undefined,
): number {
  let dmg = base + getStacks(attacker, "strength");
  if (dmg < 0) dmg = 0;
  if (getStacks(attacker, "weak") > 0) {
    dmg = Math.floor(dmg * STATUS_TUNING.weakMultiplier);
  }
  return dmg;
}

/**
 * Compute the damage a defender will actually take from an incoming raw amount,
 * BEFORE block. Applies vulnerable (multiplier), exposed (flat), firewall
 * (flat reduction), and intangible (hard cap). Returns >= 0.
 *
 * Returned value is "post-mitigation pre-block" damage. Block is then subtracted
 * by the caller so block-tracking events stay accurate.
 */
export function computeIncomingDamage(
  raw: number,
  defender: StatusMap | undefined,
): number {
  let dmg = raw;
  if (getStacks(defender, "vulnerable") > 0) {
    dmg = Math.floor(dmg * STATUS_TUNING.vulnerableMultiplier);
  }
  dmg += getStacks(defender, "exposed") * STATUS_TUNING.exposedFlatPerStack;
  const fw = getStacks(defender, "firewall");
  if (fw > 0) dmg -= fw;
  if (dmg < 0) dmg = 0;
  if (getStacks(defender, "intangible") > 0) {
    dmg = Math.min(dmg, STATUS_TUNING.intangibleCap);
  }
  return dmg;
}

/**
 * Tick statuses that decay at end-of-turn (duration types) by 1.
 * Does NOT handle DoT/regen damage (that happens at start-of-turn in combat.ts
 * so events are ordered correctly). Pure: returns a new StatusMap.
 */
export function tickEndOfTurnStatuses(s: StatusMap | undefined): StatusMap {
  const out: StatusMap = { ...(s ?? {}) };
  for (const key of Object.keys(out) as StatusKind[]) {
    const def = STATUS_DEFS[key];
    if (def.behavior === "duration") {
      const v = (out[key] ?? 0) - 1;
      if (v <= 0) delete out[key];
      else out[key] = v;
    }
  }
  return out;
}
